import React, { memo, useState } from 'react';
import Chart from 'react-apexcharts';

const MultiLineChart = memo(({ data, type }) => {
  console.log(data, 'MultiLineChartData');

  const colors =
    type === 'Payments'
      ? ['#8DFF97', '#35793A']
      : type === 'Charges'
        ? ['#3379ff', 'var(--primary)', '#FFB013']
        : [];

  const formatRange = (range) => {
    if (!range) return '';
    const [startDate, endDate] = range.split(' - ');
    if (!startDate || !endDate) return range;

    const [sm, sd] = startDate.split('/');
    const [em, ed] = endDate.split('/');

    return `${sm}/${sd} - ${em}/${ed}`;
  };

  const initialOptions = {
    chart: {
      type: 'line',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
      toolbar: { show: false },
      selection: { enabled: false },
      sparkline: { enabled: false },
    },
    markers: {
      size: 4,
      strokeColor: '#fff',
      strokeWidth: 2,
      hover: { size: 6 },
    },
    states: { active: { filter: { type: 'none' } } },
    series: data?.series || [],
    xaxis: {
      categories: data?.DateRange || [],
      labels: {
        show: true,
        rotate: 0,
        trim: false,
        offsetX: -1,
        formatter: (val) => formatRange(val),
        style: {
          fontSize: '11px',
          fontFamily: 'Poppins',
          fontWeight: '400',
          colors: '#434343',
        },
      },
      axisBorder: { show: true },
      axisTicks: { show: true },
      tooltip: { enabled: false },
      tickPlacement: 'between',
    },
    yaxis: {
      show: true,
      labels: {
        show: true,
        offsetX: -15,
        formatter: (val) => `$${(val / 1000).toFixed(0)}k`,
        style: {
          fontSize: '11px',
          fontFamily: 'Poppins',
          fontWeight: '400',
          colors: ['#434343'],
        },
      },
      tickAmount: 3,
    },
    fill: { opacity: 1 },
    plotOptions: { line: { distributed: true } },
    stroke: { width: 2, curve: 'smooth' },
    dataLabels: { enabled: false },

    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ series, w }) => {
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        const seriesNames = w.globals.seriesNames;
        const dateRanges = data?.DateRange || [];

        // Step 1: Measure max width for each column
        const columnWidths = [];
        const tempDiv = document.createElement('div');
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.position = 'absolute';
        tempDiv.style.whiteSpace = 'nowrap';
        tempDiv.style.padding = '0';
        tempDiv.style.margin = '0';
        tempDiv.style.border = '0';
        tempDiv.style.fontSize = '11px';
        tempDiv.style.fontFamily = 'Poppins';
        tempDiv.style.fontWeight = '400';
        document.body.appendChild(tempDiv);

        // Column 0 = labels
        let labelMax = 0;
        dateRanges.forEach((label) => {
          tempDiv.innerText = label;
          labelMax = Math.max(labelMax, tempDiv.getBoundingClientRect().width);
        });
        tempDiv.innerText = 'Total';
        labelMax = Math.max(labelMax, tempDiv.getBoundingClientRect().width);
        columnWidths.push(labelMax);

        // Columns 1..N = series
        for (let col = 0; col < series.length; col++) {
          let max = 0;

          // ✅ Include header name
          tempDiv.innerText = seriesNames[col];
          max = Math.max(max, tempDiv.getBoundingClientRect().width);

          // Row values
          series[col].forEach((val) => {
            tempDiv.innerText = formatter.format(val || 0);
            max = Math.max(max, tempDiv.getBoundingClientRect().width);
          });

          // Totals
          const total = series[col].reduce((sum, n) => sum + (n || 0), 0);
          tempDiv.innerText = formatter.format(total);
          max = Math.max(max, tempDiv.getBoundingClientRect().width);

          columnWidths.push(max);
        }

        tempDiv.remove();

        // Step 2: Build header
        const header = `
          <div class="d-flex justify-content-between mb-1 align-items-center gap-10">
            <div class="xsm_heading" style="width:${columnWidths[0]}px;">&nbsp;</div>
            ${seriesNames
            .map(
              (name, idx) =>
                `<div class="xsm_heading" style="width:${columnWidths[idx + 1]}px;">${name}</div>`
            )
            .join('')}
          </div>
          <div class="app_border border-end-0 border-start-0 border-top-0 mb-06-px"></div>
        `;

        // Step 3: Build rows
        const rows = dateRanges
          .map((label, i) => {
            const values = series
              .map(
                (s, idx) =>
                  `<div class="xsm_text" style="width:${columnWidths[idx + 1]}px;">${formatter.format(
                    s[i] || 0
                  )}</div>`
              )
              .join('');

            return `
              <div class="d-flex justify-content-between mt-1 align-items-center gap-10">
                <div class="xsm_text" style="width:${columnWidths[0]}px;">${label}</div>
                ${values}
              </div>
            `;
          })
          .join('');

        // Step 4: Totals row
        const totals = series.map((s) => s.reduce((sum, n) => sum + (n || 0), 0));
        const totalRow = `
          <div class="app_border border-end-0 border-start-0 border-top-0 mt-1 mb-06-px"></div>
          <div class="d-flex justify-content-between mt-1 fw-500 align-items-center gap-10">
            <div class="xsm_heading" style="width:${columnWidths[0]}px;">Total</div>
            ${totals
            .map(
              (t, idx) =>
                `<div class="xsm_heading" style="width:${columnWidths[idx + 1]}px;">${formatter.format(t)}</div>`
            )
            .join('')}
          </div>
        `;

        return `
          <div class="apexcharts-tooltip-title m-0">
            ${header}
            ${rows}
            ${totalRow}
          </div>
        `;
      },
    },

    grid: { show: true, borderColor: '#f4f4f4', strokeWidth: 0.25 },
    colors: colors,
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: 12,
      fontFamily: 'Poppins',
      fontWeight: '500',
      markers: { radius: 1, height: 10, width: 10 },
    },
  };

  const [state] = useState(initialOptions);

  return (
    <div className="position-relative ar-multiline-chart">
      <Chart
        options={state}
        series={data?.series || []}
        type={state.chart.type}
        height="140"
        width="100%"
      />
    </div>
  );
});

export default MultiLineChart;
